/**
 * FactoryResetCard — Danger-zone admin tab (Feb 2026).
 *
 * Runs the same wipe as `python -m tests.factory_reset_uat --execute` but
 * from the browser. Heavy friction:
 *   1. First click → "Preview counts" (dry-run) shows what would be deleted.
 *   2. Second click requires typing YES_WIPE into a confirmation field.
 *   3. Native browser confirm() as a final safety net.
 *   4. Backend also enforces the YES_WIPE check server-side.
 *
 * Preserves: admin@meliandialogue.com, nse_daily_bars, deployment_events.
 * Everything else in the DB gets deleted.
 */
import { useCallback, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Eye, Bomb, ShieldCheck, RefreshCw } from "lucide-react";

const CONFIRM_PHRASE = "YES_WIPE";

export default function FactoryResetCard() {
    const [phase,       setPhase]       = useState("idle");      // idle | previewed | executing | done
    const [preview,     setPreview]     = useState(null);
    const [result,      setResult]      = useState(null);
    const [phrase,      setPhrase]      = useState("");
    const [error,       setError]       = useState(null);
    const [loading,     setLoading]     = useState(false);

    const canExecute = phrase === CONFIRM_PHRASE && phase === "previewed" && !loading;

    const onDryRun = useCallback(async () => {
        setLoading(true); setError(null); setResult(null); setPhrase("");
        try {
            const { data } = await api.post("/admin/ops/factory-reset", { dry_run: true });
            setPreview(data);
            setPhase("previewed");
            toast.success(`Preview ready — ${data.total_documents.toLocaleString()} documents would be deleted.`);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
            toast.error("Dry-run failed.");
        } finally {
            setLoading(false);
        }
    }, []);

    const onExecute = useCallback(async () => {
        if (!canExecute) return;
        if (!window.confirm(
            `⚠️  This will permanently delete ${preview?.total_documents?.toLocaleString() ?? "all"} documents ` +
            `across ${Object.keys(preview?.deletions || {}).length} collections. ` +
            `Only admin@meliandialogue.com will survive.\n\nContinue?`
        )) return;

        setLoading(true); setError(null); setPhase("executing");
        try {
            const { data } = await api.post("/admin/ops/factory-reset",
                { dry_run: false, confirm: CONFIRM_PHRASE });
            setResult(data);
            setPhase("done");
            toast.success(`Factory reset complete — ${data.total_documents.toLocaleString()} documents deleted.`);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
            toast.error("Factory reset failed.");
            setPhase("previewed");
        } finally {
            setLoading(false);
        }
    }, [canExecute, preview]);

    const onReset = useCallback(() => {
        setPhase("idle"); setPreview(null); setResult(null); setPhrase(""); setError(null);
    }, []);

    return (
        <div className="space-y-4" data-testid="factory-reset-card">
            {/* Danger-zone header */}
            <div className="rounded-xl border-2 border-destructive/40 bg-destructive/5 p-4 md:p-5">
                <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-destructive text-white flex items-center justify-center shrink-0">
                        <AlertTriangle className="w-5 h-5" strokeWidth={2.5} />
                    </div>
                    <div>
                        <h2 className="text-lg font-extrabold tracking-tight text-destructive">Danger Zone · Factory Reset</h2>
                        <p className="text-xs text-muted-foreground mt-1">
                            One-click clean sweep for UAT. Wipes every user, trade, post, chat, log, notification, subscription, and audit event across the entire database. Preserves only <b>admin@meliandialogue.com</b>, market chart history, and deployment audit trail.
                        </p>
                    </div>
                </div>

                {/* Preserve/wipe legend */}
                <div className="grid md:grid-cols-2 gap-3 mb-4 text-xs">
                    <div className="rounded-lg border border-border bg-card p-3">
                        <div className="text-[10px] uppercase font-bold text-emerald-600 mb-1.5 tracking-widest">✓ Preserved</div>
                        <ul className="space-y-0.5 text-muted-foreground">
                            <li>• <b>admin@meliandialogue.com</b> user + auth</li>
                            <li>• Chart history (<code className="text-[10px]">nse_daily_bars</code>)</li>
                            <li>• Deployment ops history</li>
                            <li>• All market simulator code (prices, catalog, taxonomy)</li>
                        </ul>
                    </div>
                    <div className="rounded-lg border border-destructive/30 bg-destructive/[0.04] p-3">
                        <div className="text-[10px] uppercase font-bold text-destructive mb-1.5 tracking-widest">✗ Wiped</div>
                        <ul className="space-y-0.5 text-muted-foreground">
                            <li>• All users (except admin), all trades &amp; positions</li>
                            <li>• Community posts, follows, chats, notifications</li>
                            <li>• Subscriptions, agencies, revenue events</li>
                            <li>• All 4 telemetry collections + audit log</li>
                        </ul>
                    </div>
                </div>

                {/* Step 1 — dry run */}
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={onDryRun}
                        disabled={loading}
                        data-testid="factory-reset-preview"
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border bg-card font-semibold text-sm hover:bg-muted/50 disabled:opacity-50"
                    >
                        {loading && phase === "idle" ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                        Preview Counts (dry-run)
                    </button>
                    {(phase !== "idle") && (
                        <button
                            onClick={onReset}
                            className="text-xs text-muted-foreground hover:text-foreground underline"
                            data-testid="factory-reset-clear"
                        >Clear</button>
                    )}
                </div>
            </div>

            {error && (
                <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive" data-testid="factory-reset-error">
                    {error}
                </div>
            )}

            {/* Preview table */}
            {preview && (
                <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="factory-reset-preview-table">
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                        <div>
                            <div className="text-[10px] uppercase font-bold text-primary tracking-widest">Dry-run preview</div>
                            <div className="text-lg font-extrabold">{preview.total_documents.toLocaleString()} documents would be deleted</div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {Object.keys(preview.deletions).length} collections
                        </div>
                    </div>
                    <div className="max-h-72 overflow-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40">
                                <tr>
                                    <th className="text-left px-4 py-1.5 text-[10px] uppercase tracking-widest font-bold">Collection</th>
                                    <th className="text-right px-4 py-1.5 text-[10px] uppercase tracking-widest font-bold">Count</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(preview.deletions)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([k, v]) => (
                                        <tr key={k} className="border-b border-border">
                                            <td className="px-4 py-1.5 font-mono text-xs">{k}</td>
                                            <td className="px-4 py-1.5 text-right font-mono proportional-nums">{v.toLocaleString()}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Step 2 — confirmation + execute */}
            {phase === "previewed" && (
                <div className="rounded-xl border-2 border-destructive/50 bg-destructive/5 p-4">
                    <label className="block text-sm font-semibold text-destructive mb-2">
                        Type <code className="px-1.5 py-0.5 rounded bg-destructive/10 font-mono">{CONFIRM_PHRASE}</code> to enable the execute button:
                    </label>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <input
                            type="text"
                            value={phrase}
                            onChange={(e) => setPhrase(e.target.value)}
                            placeholder={CONFIRM_PHRASE}
                            data-testid="factory-reset-phrase"
                            className="flex-1 h-10 px-3 rounded border border-destructive/40 bg-background font-mono text-sm outline-none focus:border-destructive"
                            autoComplete="off"
                            spellCheck={false}
                        />
                        <button
                            onClick={onExecute}
                            disabled={!canExecute}
                            data-testid="factory-reset-execute"
                            className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-lg bg-destructive text-white font-bold text-sm hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Bomb className="w-4 h-4" />}
                            Execute Factory Reset
                        </button>
                    </div>
                    <p className="text-[10px] text-destructive/80 mt-2 leading-snug">
                        This action is <b>irreversible</b>. Every non-admin user, trade, post, chat, and log will be permanently deleted.
                    </p>
                </div>
            )}

            {/* Result */}
            {phase === "done" && result?.post_reset && (
                <div className="rounded-xl border-2 border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="factory-reset-result">
                    <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500 text-white flex items-center justify-center shrink-0">
                            <ShieldCheck className="w-5 h-5" strokeWidth={2.5} />
                        </div>
                        <div>
                            <h3 className="text-lg font-extrabold tracking-tight text-emerald-700 dark:text-emerald-400">Factory reset complete.</h3>
                            <p className="text-xs text-muted-foreground mt-1">
                                {result.total_documents.toLocaleString()} documents deleted. Platform is UAT-ready.
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <Stat label="Surviving users"      value={result.post_reset.surviving_users} testId="post-users" />
                        <Stat label="Agencies remaining"   value={result.post_reset.agencies_left}   testId="post-agencies" />
                        <Stat label="Revenue locked-in"    value={`KES ${result.post_reset.revenue_kes.toLocaleString("en-KE")}`} testId="post-revenue" />
                        <Stat label="Admin preserved"      value={result.post_reset.admin_email ? "✓" : "✗"}     testId="post-admin" />
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, testId }) {
    return (
        <div className="rounded-lg border border-border bg-card p-2">
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="font-mono font-bold text-sm whitespace-nowrap" data-testid={testId}>{String(value)}</div>
        </div>
    );
}
